<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="$t('ncrm.activities.createTask.title')"
    :subtitle="$t('ncrm.activities.createTask.subtitle')"
    :loading="submitting"
    :save-disabled="submitDisabled"
    :max-width="620"
    :extra-action-label="$t('app.actions.clear')"
    :extra-action-disabled="submitting"
    @update:model-value="onDialogToggle"
    @extra-action="resetForm"
    @save="save"
  >
    <v-row dense>
      <v-col cols="12">
        <v-autocomplete
          v-model="form.owner_user_id"
          :items="ownerOptions"
          item-title="label"
          item-value="id"
          :label="$t('ncrm.activities.createTask.fields.assignee')"
          :menu-props="menuProps"
          clearable
        />
      </v-col>

      <v-col cols="12" md="5">
        <v-select
          v-model="form.link_type"
          :items="linkTypeItems"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.activities.createTask.fields.linkType')"
          :menu-props="menuProps"
        />
      </v-col>
      <v-col v-if="form.link_type !== 'none'" cols="12" md="7">
        <v-autocomplete
          v-model="form.entity_id"
          :items="entityOptions"
          item-title="label"
          item-value="id"
          :label="entityPickerLabel"
          :loading="entityLoading"
          :menu-props="menuProps"
          no-filter
          clearable
          @update:search="onEntitySearch"
        />
      </v-col>

      <v-col cols="12" :md="showStatusField ? 6 : 12">
        <v-select
          v-model="form.activity_type"
          :items="activityTypeItems"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.activity.fields.type')"
          :menu-props="menuProps"
        />
      </v-col>
      <v-col v-if="showStatusField" cols="12" md="6">
        <v-select
          v-model="form.status"
          :items="activityStatusItems"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.activity.fields.status')"
          :menu-props="menuProps"
        />
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="form.title"
          :label="$t('ncrm.activity.fields.title')"
        />
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="form.content"
          :label="$t('ncrm.activity.fields.content')"
          rows="3"
          auto-grow
        />
      </v-col>

      <v-col v-if="requiresDueAt" cols="12" md="8">
        <NcrmDateTimeField v-model="form.due_at" :label="$t('ncrm.activity.fields.dueAtRequired')" />
      </v-col>
      <v-col v-if="showPriorityField" cols="12" :md="requiresDueAt ? 4 : 12">
        <v-select
          v-model="form.priority"
          :items="activityPriorityItems"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.activity.fields.priority')"
          :menu-props="menuProps"
        />
      </v-col>
      <v-col v-if="requiresDueAt" cols="12">
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.activity.validation.dueRequired") }}
        </div>
      </v-col>
    </v-row>
  </NcrmRecordEditDialog>
</template>

<script>
import axios from "axios";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  buildNcrmActivityCreatePayload,
  getNcrmActivityPriorityOptions,
  getNcrmActivityStatusOptions,
  getNcrmActivityTypeOptions,
  isNcrmActivityProgrammableType,
  normalizeNcrmActivityType,
  resolveNcrmActivityDefaultStatus,
} from "@/Utile/ncrmActivity.mjs";

const buildDefaultForm = () => ({
  activity_type: "todo",
  status: "open",
  priority: "normal",
  title: "",
  content: "",
  due_at: "",
  owner_user_id: null,
  link_type: "none",
  entity_id: null,
});

export default {
  name: "NcrmCreateTaskDialog",
  components: {
    NcrmDateTimeField,
    NcrmRecordEditDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    ownerOptions: {
      type: Array,
      default: () => [],
    },
    defaultAssigneeId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["update:modelValue", "created", "error"],
  data() {
    return {
      form: buildDefaultForm(),
      submitting: false,
      entityOptions: [],
      entityLoading: false,
      entitySearchDebounceId: null,
      menuProps: { contentClass: "ncrm-create-task__menu", zIndex: 2710 },
    };
  },
  computed: {
    isProgrammableType() {
      return isNcrmActivityProgrammableType(this.form.activity_type);
    },
    requiresDueAt() {
      return this.isProgrammableType;
    },
    showStatusField() {
      return this.isProgrammableType;
    },
    showPriorityField() {
      return this.isProgrammableType;
    },
    activityTypeItems() {
      return getNcrmActivityTypeOptions().map((type) => ({
        value: type,
        label: this.activityTypeLabel(type),
      }));
    },
    activityStatusItems() {
      const type = normalizeNcrmActivityType(this.form.activity_type, "todo") || "todo";
      return getNcrmActivityStatusOptions(type).map((status) => ({
        value: status,
        label: this.activityStatusLabel(status, type),
      }));
    },
    activityPriorityItems() {
      return getNcrmActivityPriorityOptions().map((priority) => ({
        value: priority,
        label: this.activityPriorityLabel(priority),
      }));
    },
    linkTypeItems() {
      return [
        { value: "none", label: this.$t("ncrm.activities.createTask.linkType.none") },
        { value: "company", label: this.$t("ncrm.activities.createTask.linkType.company") },
        { value: "person", label: this.$t("ncrm.activities.createTask.linkType.person") },
      ];
    },
    entityPickerLabel() {
      if (this.form.link_type === "company") {
        return this.$t("ncrm.activities.createTask.fields.linkedCompany");
      }
      if (this.form.link_type === "person") {
        return this.$t("ncrm.activities.createTask.fields.linkedPerson");
      }
      return this.$t("ncrm.activities.createTask.fields.linkedEntity");
    },
    submitDisabled() {
      if (this.submitting) return true;
      if (!this.parsePositiveInt(this.form.owner_user_id)) return true;
      if (!String(this.form.content || "").trim()) return true;
      if (this.requiresDueAt && !String(this.form.due_at || "").trim()) return true;
      if (this.form.link_type !== "none" && !this.parsePositiveInt(this.form.entity_id)) return true;
      return false;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.resetForm();
      } else {
        this.clearEntitySearchDebounce();
      }
    },
    "form.activity_type"(nextType) {
      const normalized = normalizeNcrmActivityType(nextType, "note");
      this.form.status = resolveNcrmActivityDefaultStatus(normalized);
    },
    "form.link_type"(nextType) {
      this.form.entity_id = null;
      this.entityOptions = [];
      this.clearEntitySearchDebounce();
      if (nextType === "company" || nextType === "person") {
        this.fetchEntityOptions("");
      }
    },
  },
  beforeUnmount() {
    this.clearEntitySearchDebounce();
  },
  methods: {
    parsePositiveInt(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return null;
      const normalized = Math.trunc(parsed);
      return normalized > 0 ? normalized : null;
    },
    activityTypeLabel(type) {
      const key = `ncrm.activity.types.${type}`;
      const translated = this.$t(key);
      return translated === key ? type : translated;
    },
    activityStatusLabel(status, type) {
      const key = `ncrm.activity.statuses.${status}`;
      const translated = this.$t(key);
      void type;
      return translated === key ? status : translated;
    },
    activityPriorityLabel(priority) {
      const key = `ncrm.activity.priorities.${priority}`;
      const translated = this.$t(key);
      return translated === key ? priority : translated;
    },
    resetForm() {
      this.clearEntitySearchDebounce();
      this.form = buildDefaultForm();
      this.entityOptions = [];
      const defaultId = this.parsePositiveInt(this.defaultAssigneeId);
      if (defaultId) {
        this.form.owner_user_id = defaultId;
      }
    },
    onDialogToggle(value) {
      this.$emit("update:modelValue", Boolean(value));
    },
    clearEntitySearchDebounce() {
      if (!this.entitySearchDebounceId) return;
      clearTimeout(this.entitySearchDebounceId);
      this.entitySearchDebounceId = null;
    },
    onEntitySearch(query) {
      if (this.form.link_type !== "company" && this.form.link_type !== "person") return;
      const term = String(query || "").trim();
      // Daca textul de cautare coincide cu eticheta optiunii deja selectate,
      // nu re-cautam (eveniment generat de selectia in sine).
      const selected = this.entityOptions.find((opt) => opt.id === this.form.entity_id);
      if (selected && selected.label === term) return;
      this.clearEntitySearchDebounce();
      this.entitySearchDebounceId = setTimeout(() => {
        this.entitySearchDebounceId = null;
        this.fetchEntityOptions(term);
      }, 300);
    },
    async fetchEntityOptions(term) {
      const type = this.form.link_type;
      if (type !== "company" && type !== "person") return;
      this.entityLoading = true;
      try {
        const { data } = await axios.get("/ncrm/contacts", {
          params: { type, search: term, perPage: 20 },
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        this.entityOptions = items
          .map((row) => this.normalizeEntityOption(row, type))
          .filter(Boolean);
      } catch (_) {
        this.entityOptions = [];
      } finally {
        this.entityLoading = false;
      }
    },
    normalizeEntityOption(row = {}, type) {
      const id = this.parsePositiveInt(row?.id);
      if (!id) return null;
      let label = "";
      if (type === "company") {
        label = String(row?.name || "").trim();
      } else {
        label = [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim();
      }
      if (!label) {
        label = String(row?.email_primary || "").trim() || `#${id}`;
      }
      return { id, label };
    },
    buildPayload() {
      const base = {
        ownerUserId: this.parsePositiveInt(this.form.owner_user_id),
        activityType: this.form.activity_type,
        status: this.form.status,
        priority: this.form.priority,
        title: this.form.title,
        content: this.form.content,
        dueAt: this.form.due_at,
      };
      if (this.form.link_type === "company") {
        base.entityType = "company";
        base.entityId = this.parsePositiveInt(this.form.entity_id);
      } else if (this.form.link_type === "person") {
        base.entityType = "person";
        base.entityId = this.parsePositiveInt(this.form.entity_id);
      } else {
        base.entityType = "user";
        base.entityId = this.parsePositiveInt(this.form.owner_user_id);
      }
      return buildNcrmActivityCreatePayload(base);
    },
    async save() {
      if (this.submitDisabled) return;
      this.submitting = true;
      try {
        const payload = this.buildPayload();
        const { data } = await axios.post("/ncrm/activity", payload);
        this.$emit("created", data || payload);
        this.$emit("update:modelValue", false);
        this.resetForm();
      } catch (error) {
        this.$emit("error", error);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
